/*
 * Component - v2.1.0
 */

// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive} from '@supermove/hooks';
import {BillModel, BillTipModel, ProjectModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';
import {Currency} from '@supermove/utils';

// App
import BillTipActionsButton from 'modules/Project/Billing/BillTip/components/BillTipActionsButton';

const Row = Styled.View`
  flex-direction: row;
`;

const LabelText = Styled.Text`
  ${Typography.Responsive.Label}
`;

const EmptySpace = Styled.View`
  flex: 6;
`;

const BillTipsContainer = Styled.View<{isEditable: boolean}>`
  flex: 7;
  border-width: ${({isEditable}) => (isEditable ? '1px' : '0px')};
  border-color: ${colors.gray.border};
  border-radius: ${({isEditable}) => (isEditable ? '8px' : '0px')};
  background-color: ${colors.white};
`;

const NameContainer = Styled.View<{isEditable: boolean; isFirst: boolean}>`
  flex: 1;
  padding-top: ${(props) => (props.isEditable && props.isFirst ? '8px' : '0px')};
  padding-bottom: ${(props) => (props.isEditable ? '8px' : '0px')};
  padding-left: ${(props) => (!props.isEditable ? '0px' : '12px')};
  flex-direction: row;
  align-items: center;
`;

const NameRow = Styled.View<{mobile: boolean; isWidget?: boolean}>`
  flex: 1;
  flex-direction: row;
  justify-content: ${(props) => (props.mobile || props.isWidget ? 'flex-start' : 'flex-end')};
`;

const AmountContainer = Styled.View<{isEditable: boolean; isFirst: boolean}>`
  justify-content: center;
  align-items: flex-end;
  padding-top: ${(props) => (props.isEditable && props.isFirst ? '8px' : '0px')};
  padding-bottom: ${(props) => (props.isEditable ? '8px' : '0px')};
  padding-horizontal: ${(props) => (props.isEditable ? '12px' : '0px')};
`;

const BillTipName = ({
  isEditable,
  billTip,
  refetch,
  isFirst,
  billId,
  project,
  isWidget,
}: {
  isEditable: boolean;
  billTip: BillTipModel;
  refetch: () => void;
  isFirst: boolean;
  billId?: string;
  project: ProjectModel;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();
  return (
    <NameContainer isEditable={isEditable} isFirst={isFirst} {...responsive}>
      {isEditable ? (
        <BillTipActionsButton
          billTip={billTip}
          billId={billId}
          refetch={refetch}
          project={project}
        />
      ) : (
        <Space />
      )}
      <NameRow {...responsive} isWidget={isWidget}>
        <Space width={8} />
        <LabelText responsive={responsive}>Tip</LabelText>
        {!!billTip.name && (
          <React.Fragment>
            <Space width={4} />
            <LabelText numberOfLines={1} responsive={responsive}>{`(${billTip.name}`}</LabelText>
            <LabelText responsive={responsive}>{`)`}</LabelText>
          </React.Fragment>
        )}
      </NameRow>
    </NameContainer>
  );
};

const BillTipAmount = ({
  billTip,
  isEditable,
  isFirst,
  isWidget,
}: {
  billTip: BillTipModel;
  isEditable: boolean;
  isFirst: boolean;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();

  return (
    <AmountContainer
      isEditable={isEditable}
      isFirst={isFirst}
      style={!responsive.mobile && !isWidget && {flex: 1}}
    >
      {!!billTip && (
        <LabelText responsive={responsive}>{Currency.format({value: billTip.amount})}</LabelText>
      )}
    </AmountContainer>
  );
};

const BillTip = ({
  billTip,
  isEditable,
  isFirst,
  refetch,
  project,
  isWidget,
}: {
  billTip: BillTipModel;
  isEditable: boolean;
  isFirst: boolean;
  refetch: () => void;
  project: ProjectModel;
  isWidget?: boolean;
}) => {
  return (
    <Row>
      <BillTipName
        billTip={billTip}
        isEditable={isEditable}
        isFirst={isFirst}
        refetch={refetch}
        project={project}
        isWidget={isWidget}
      />
      <BillTipAmount
        billTip={billTip}
        isEditable={isEditable}
        isFirst={isFirst}
        isWidget={isWidget}
      />
    </Row>
  );
};

const EmptyBillTip = ({
  isEditable,
  primaryBill,
  refetch,
  project,
  isWidget,
}: {
  isEditable: boolean;
  primaryBill: BillModel;
  refetch: () => void;
  project: ProjectModel;
  isWidget?: boolean;
}) => {
  const responsive = useResponsive();
  return (
    <Row>
      <NameContainer isEditable={isEditable} isFirst {...responsive}>
        <BillTipActionsButton billId={primaryBill.id} refetch={refetch} project={project} />
        <NameRow {...responsive}>
          <Space width={8} />
          <LabelText responsive={responsive}>Tip</LabelText>
        </NameRow>
      </NameContainer>
      <AmountContainer
        isEditable={isEditable}
        isFirst
        style={!responsive.mobile && !isWidget && {flex: 1}}
      />
    </Row>
  );
};

const ProjectBillTipsList = ({
  primaryBill,
  isEditable,
  refetch,
  project,
  isWidget,
}: {
  primaryBill: BillModel;
  isEditable: boolean;
  refetch: () => void;
  project: ProjectModel;
  isWidget?: boolean;
}) => {
  const {billTips} = primaryBill;
  const responsive = useResponsive();
  return (
    <Row>
      {!responsive.mobile && !isWidget && <EmptySpace />}
      <BillTipsContainer isEditable={isEditable}>
        {billTips.length === 0 ? (
          isEditable ? (
            <EmptyBillTip
              project={project}
              isEditable={isEditable}
              primaryBill={primaryBill}
              refetch={refetch}
              isWidget={isWidget}
            />
          ) : null
        ) : (
          billTips.map((billTip: any, index: any) => {
            const isFirst = index === 0;
            return (
              <React.Fragment key={billTip.id}>
                {!isEditable && !isFirst && <Space height={8} />}
                <BillTip
                  billTip={billTip}
                  isEditable={isEditable}
                  project={project}
                  isFirst={isFirst}
                  refetch={refetch}
                  isWidget={isWidget}
                />
              </React.Fragment>
            );
          })
        )}
      </BillTipsContainer>
    </Row>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectBillTipsList.fragment = gql`
  ${BillTipActionsButton.fragment}

  fragment ProjectBillTipsList on Bill {
    id
    billTips {
      id
      name
      amount
      ...BillTipActionsButton
    }
  }

  fragment ProjectBillTipsList_Project on Project {
    id
    ...BillTipActionsButton_Project
  }
`;

export default ProjectBillTipsList;
